import { Endpoint, Graph, Api } from '@square/ignition';
import { ApiExplorerInput } from '@squareup/dex-types-shared-api-explorer';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { SchemaType } from '@squareup/dex-utils-shared-api-explorer';
import { JSONPatchOperation } from 'immutable-json-patch';
import { Dispatch, ReactNode, createContext } from 'react';

interface InputReducerPatchAction {
  type: 'patch';
  operation: JSONPatchOperation;
}

interface InputReducerSetAction {
  type: 'set';
  newState: ApiExplorerInput;
}

type InputReducerAction = InputReducerPatchAction | InputReducerSetAction;

interface ApiExplorerApiResponse {
  status: number | undefined;
  body?: unknown | undefined;
  headers?: Record<string, string> | undefined;
}

interface ApiExplorerFieldDropdownOption {
  value: string;
  displayText?: string | undefined;
  description?: ReactNode | undefined;
}

interface ApiExplorerAccessTokenContext {
  token: string;
  productionClient: string;
  environment: ApplicationType;
}

type AccessTokenAction =
  | {
      type: 'SET';
      payload: ApiExplorerAccessTokenContext;
    }
  | {
      type: 'SET_ENVIRONMENT';
      payload: ApiExplorerAccessTokenContext['environment'];
    }
  | {
      type: 'SET_TOKEN';
      payload: ApiExplorerAccessTokenContext['token'];
    }
  | {
      type: 'SET_PRODUCTION_CLIENT';
      payload: ApiExplorerAccessTokenContext['productionClient'];
    };

interface ApiExplorerContext {
  input: ApiExplorerInput;
  dispatchInputAction: Dispatch<InputReducerAction>;
  graph: Graph;
  endpoint?: Endpoint; // Endpoint will be undefined on the home page (/explorer/square)
  api?: Api; // Api will be undefined on the home page (/explorer/square)
  apiResponse?: ApiExplorerApiResponse | null;
  onApiRequestInitiated?: () => void;
  isApiRequestRunning?: boolean;
  accessTokenData: ApiExplorerAccessTokenContext;
  dispatchAccessTokenAction: Dispatch<AccessTokenAction>;
  apiExplorerFieldDropdownOptionsMap: Record<
    string,
    Array<ApiExplorerFieldDropdownOption>
  >;
  customAttributeSchema?: SchemaType | undefined;
}

const ApiExplorerContextInstance = createContext<ApiExplorerContext>({
  input: {
    bodyParameters: {},
    queryParameters: {},
    pathParameters: {},
    authParameters: {},
    fileParameters: {},
  },
  dispatchInputAction: (_) => null,
  // Making this typesafe for children. It is guaranteed to be replaced
  // by a real value in the container. This simplifies a lot of checks later
  graph: {} as unknown as Graph,
  endpoint: undefined as unknown as Endpoint,
  api: undefined as unknown as Api,
  accessTokenData: {
    token: '',
    productionClient: '',
    environment: ApplicationType.Sandbox,
  } as ApiExplorerAccessTokenContext,
  dispatchAccessTokenAction: (_) => null,
  apiExplorerFieldDropdownOptionsMap: {},
  customAttributeSchema: undefined as unknown as SchemaType,
});

const ApiExplorerContextProvider = ApiExplorerContextInstance.Provider;

export {
  ApiExplorerContextProvider,
  type ApiExplorerContext,
  ApiExplorerContextInstance,
  type InputReducerAction,
  type InputReducerPatchAction,
  type InputReducerSetAction,
  type ApiExplorerApiResponse,
  type ApiExplorerFieldDropdownOption,
  type ApiExplorerAccessTokenContext,
  type AccessTokenAction,
};
